import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DashboardCard from '../DashboardCard';
import BackupAccount from './BackupAccount';
import * as bip39 from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import hive from 'helpers/hive';
import { getUser } from 'helpers/token';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import validateAccountName from 'helpers/validateAccountName';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import { CREATE_HIVE_ACCOUNT } from 'helpers/graphql/hive';
import { useMutation } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import Link from 'next/link';

const steps = ['Pick a username', 'Backup your account'];

export default function HorizontalLinearStepper() {
  const [activeStep, setActiveStep] = useState(0);
  const [username, setUsername] = useState('');
  const [keys, setKeys] = useState(undefined);
  const [showValidation, setShowValidation] = useState(false);
  const [available, setAvailable] = useState(undefined);
  const [showUsernamePicker, setShowUsernamePicker] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [created, setCreated] = useState(undefined);

  useEffect(() => {
    const tfUser = getUser();
    setUsername(tfUser);
    checkUsernameAvailability(tfUser);
  }, []);

  const [createHiveAccount] = useMutation(CREATE_HIVE_ACCOUNT);

  function accountCreation() {
    setCreated(undefined);
    createHiveAccount({
      variables: { username, ...keys.publicKeys },
    })
      .then(({ data }) => {
        if (data.createHiveAccount.success) {
          setCreated(true);
        } else {
          console.error(data);
          setCreated(false);
        }
      })
      .catch(err => {
        console.error(err);
        setCreated(false);
      });
  }

  const { enqueueSnackbar } = useSnackbar();

  const newNotification = notification => {
    if (notification !== undefined) {
      let variant = 'success';
      if (notification.success === false) {
        variant = 'error';
      }
      enqueueSnackbar(notification.message, { variant });
    }
  };

  function checkUsernameAvailability(user) {
    return hive.api
      .getAccountsAsync([user])
      .then(account => {
        if (account && account.length > 0) {
          setAvailable(false);
          setShowUsernamePicker(true);
          return false;
        } else {
          setAvailable(true);
          return true;
        }
      })
      .catch(err => console.error(err));
  }

  function generatePassphrase() {
    setIsDownloaded(false);
    const mn = bip39.generateMnemonic(wordlist);
    setKeys({
      password: mn,
      publicKeys: hive.auth.generateKeys(username, mn, [
        'owner',
        'active',
        'posting',
        'memo',
      ]),
      privateKeys: hive.auth.getPrivateKeys(username, mn, [
        'owner',
        'active',
        'posting',
        'memo',
      ]),
    });
  }

  const handleNext = async () => {
    if (activeStep === 0) {
      const isAvailable = await checkUsernameAvailability(username);
      setAvailable(isAvailable);
      if (!isAvailable || !username || validateAccountName(username) !== null) {
        setShowValidation(true);
        newNotification({
          success: false,
          message: 'Please pick a valid Hive username',
        });
        return;
      }
      generatePassphrase();
    } else if (activeStep === 1) {
      if (!isDownloaded) {
        newNotification({
          success: false,
          message: 'Please download your keys first',
        });
        return;
      } else accountCreation();
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  return (
    <div className="container d-flex justify-content-center mt-5 mb-5">
      <div style={{ maxWidth: '800px' }}>
        <DashboardCard
          titleSize="h5"
          title="Create a free Hive account"
          content={
            <Box sx={{ width: '100%' }}>
              <Stepper activeStep={activeStep}>
                {steps.map(label => {
                  const stepProps = {};
                  const labelProps = {};
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              {activeStep === steps.length ? (
                <React.Fragment>
                  {created === undefined ? (
                    <div className="text-center pt-5 tw-w-full">
                      <CircularProgress />
                    </div>
                  ) : created ? (
                    <div className="postcontent">
                      <Typography sx={{ mt: 2, mb: 1 }}>
                        Congratulations! Your Hive account @{username} has been
                        created successfully and linked to TravelFeed! The Hive
                        integration works seamlessly in the background – just
                        keep using TravelFeed as before.
                      </Typography>
                      <Typography sx={{ mt: 2, mb: 1 }}>
                        If you want, you can publish your existing TravelFeed
                        posts to Hive by editing them.
                      </Typography>
                      <Typography sx={{ mt: 2, mb: 1 }}>
                        If you want to learn more about Hive, we recommend these
                        resources:
                        <ul>
                          <li>
                            Install the{' '}
                            <a
                              href="https://hive-keychain.com/"
                              target="_blank"
                              rel="nofollow noreferrer noopener"
                            >
                              Hive Keychain
                            </a>{' '}
                            browser extension and/or smartphone app and add your
                            private keys from the previous step. You can use
                            this to access your rewards and log in to Hive apps.
                          </li>
                          <li>
                            Explore{' '}
                            <a
                              href="https://hive.io/eco/"
                              target="_blank"
                              rel="nofollow noreferrer noopener"
                            >
                              other apps built on Hive
                            </a>
                          </li>
                          <li>
                            Read this{' '}
                            <a
                              href="https://peakd.com/hive-167922/@jfuji/what-is-hive-a-10-minute-guide"
                              target="_blank"
                              rel="nofollow noreferrer noopener"
                            >
                              Hive starting guide
                            </a>
                          </li>
                        </ul>
                      </Typography>
                    </div>
                  ) : (
                    <div className="postcontent">
                      <Typography sx={{ mt: 2, mb: 1 }}>
                        Something went wrong when creating your account. Please
                        try again or{' '}
                        <Link href="/about/contact">contact support</Link>.
                      </Typography>
                    </div>
                  )}
                </React.Fragment>
              ) : (
                <>
                  {activeStep === 0 ? (
                    <>
                      <div className="postcontent tw-p-2 tw-pt-4">
                        <h3>
                          What is Hive and Why Should You Create an Account?
                        </h3>
                        <p>
                          Hive is a decentralized social network. This means
                          that there are various websites that all share the
                          same login credentials, and the same content is
                          accessible on all of them.
                        </p>
                        <p>
                          TravelFeed is part of Hive. By setting up your Hive
                          account, all your posts, comments, and upvotes on
                          TravelFeed will also appear on other websites and apps
                          powered by Hive. This allows your content to reach a
                          larger audience. If you have a travel blog on your own
                          domain, consider the potential SEO implications and{' '}
                          <a
                            href="/help/article/visibility#hive-settings"
                            target="_blank"
                          >
                            adjust your Hive settings
                          </a>{' '}
                          after creating your account.
                        </p>
                        <p>
                          Hive also has a built-in feature to monetize your
                          content. You can receive rewards based on the upvotes
                          your content receives.
                        </p>
                        <p>
                          Ready to create your Hive account? Let&apos;s get
                          started! First, let&apos;s pick a username. Your Hive
                          username can be different from your TravelFeed
                          username.
                        </p>
                        {available !== undefined ? (
                          available ? (
                            <>
                              {showUsernamePicker ? (
                                <></>
                              ) : (
                                <>
                                  <p>
                                    Your username <strong>{username}</strong> is
                                    available on Hive! Click next to continue.
                                  </p>
                                  <Button
                                    onClick={() => setShowUsernamePicker(true)}
                                  >
                                    Pick a different username
                                  </Button>
                                </>
                              )}
                            </>
                          ) : (
                            <p>
                              Your username {username} is not available on Hive.
                              Please pick a different username.
                            </p>
                          )
                        ) : (
                          <></>
                        )}
                      </div>
                      {showUsernamePicker && (
                        <>
                          <FormControl
                            variant="standard"
                            fullWidth
                            error={
                              showValidation &&
                              (!available ||
                                validateAccountName(username) !== null ||
                                !username)
                            }
                          >
                            <TextField
                              autoFocus
                              error={
                                showValidation &&
                                (!available ||
                                  validateAccountName(username) !== null ||
                                  !username)
                              }
                              fullWidth
                              inputProps={{
                                maxLength: 16,
                              }}
                              id="standard-name"
                              value={username}
                              placeholder="Pick a username"
                              label="Hive Username"
                              onChange={e => {
                                setUsername(e.target.value);
                                setAvailable(true);
                              }}
                              margin="normal"
                              variant="outlined"
                            />
                            {showValidation &&
                              (!available ||
                                validateAccountName(username) !== null ||
                                !username) && (
                                <>
                                  <FormHelperText>
                                    {validateAccountName(username) === null
                                      ? 'Username is taken'
                                      : validateAccountName(username)}
                                  </FormHelperText>
                                </>
                              )}
                          </FormControl>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="p-2">
                      <BackupAccount
                        username={username}
                        keys={keys}
                        newNotification={newNotification}
                        setIsDownloaded={setIsDownloaded}
                      />
                    </div>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />

                    <Button onClick={handleNext} variant="contained">
                      {activeStep === steps.length - 1
                        ? 'Create Account'
                        : 'Next'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          }
        />
      </div>
    </div>
  );
}
